<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  imageFile: {
    type: File,
    default: null
  },
  isUploading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'upload'])

// Canvas and image refs
const canvasRef = ref(null)
const imageDataUrl = ref(null)
const originalImage = ref(null)

// Crop state
const cropPosition = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const scale = ref(1)

// Crop box dimensions (square)
const CROP_SIZE = 300

// Load image when file changes
watch(() => props.imageFile, (newFile) => {
  if (newFile && props.show) {
    loadImage(newFile)
  }
}, { immediate: true })

watch(() => props.show, (isShown) => {
  if (isShown && props.imageFile) {
    loadImage(props.imageFile)
  }
})

const loadImage = (file) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      originalImage.value = img
      imageDataUrl.value = e.target.result

      // Initialize crop position to center
      const canvas = canvasRef.value
      if (canvas) {
        canvas.width = CROP_SIZE * 2 // Higher resolution for better quality
        canvas.height = CROP_SIZE * 2
      }

      // Calculate initial scale to fit image
      const scaleX = CROP_SIZE / img.width
      const scaleY = CROP_SIZE / img.height
      scale.value = Math.max(scaleX, scaleY) * 1.2 // Slightly larger than minimum

      // Center the image
      const scaledWidth = img.width * scale.value
      const scaledHeight = img.height * scale.value
      cropPosition.value = {
        x: (CROP_SIZE - scaledWidth) / 2,
        y: (CROP_SIZE - scaledHeight) / 2
      }

      drawCanvas()
    }
    img.src = e.target.result
  }
  reader.readAsDataURL(file)
}

const drawCanvas = () => {
  const canvas = canvasRef.value
  const img = originalImage.value

  if (!canvas || !img) return

  const ctx = canvas.getContext('2d')

  // Fill with white background first
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Draw image with current position and scale
  const scaledWidth = img.width * scale.value
  const scaledHeight = img.height * scale.value

  // Calculate position for 2x resolution
  const drawX = cropPosition.value.x * 2
  const drawY = cropPosition.value.y * 2
  const drawWidth = scaledWidth * 2
  const drawHeight = scaledHeight * 2

  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
}

// Mouse/touch drag handlers
const handleMouseDown = (e) => {
  isDragging.value = true
  dragStart.value = {
    x: e.clientX - cropPosition.value.x,
    y: e.clientY - cropPosition.value.y
  }
}

const handleMouseMove = (e) => {
  if (!isDragging.value) return

  cropPosition.value = {
    x: e.clientX - dragStart.value.x,
    y: e.clientY - dragStart.value.y
  }

  drawCanvas()
}

const handleMouseUp = () => {
  isDragging.value = false
}

// Zoom handlers
const handleZoomIn = () => {
  scale.value = Math.min(scale.value + 0.1, 3)
  drawCanvas()
}

const handleZoomOut = () => {
  const img = originalImage.value
  if (!img) return

  const minScaleX = CROP_SIZE / img.width
  const minScaleY = CROP_SIZE / img.height
  const minScale = Math.max(minScaleX, minScaleY)

  scale.value = Math.max(scale.value - 0.1, minScale)
  drawCanvas()
}

// Upload the cropped image
const handleUpload = () => {
  const canvas = canvasRef.value
  if (!canvas) return

  canvas.toBlob((blob) => {
    if (blob) {
      emit('upload', blob, props.imageFile.name)
    }
  }, 'image/jpeg', 0.9)
}

const handleCancel = () => {
  // Reset state
  imageDataUrl.value = null
  originalImage.value = null
  cropPosition.value = { x: 0, y: 0 }
  scale.value = 1

  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          @click="handleCancel"
        ></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full transform transition-all"
            @click.stop
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
          >
            <!-- Header -->
            <div class="border-b border-gray-200 px-6 py-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-xl font-bold text-gray-900">Crop Profile Picture</h3>
                  <p class="text-sm text-gray-500 mt-1">Drag to reposition, use zoom controls to adjust</p>
                </div>
                <button
                  type="button"
                  @click="handleCancel"
                  class="text-gray-400 hover:text-gray-500 transition-colors"
                  :disabled="isUploading"
                >
                  <span class="material-symbols-outlined text-2xl">close</span>
                </button>
              </div>
            </div>

            <!-- Body -->
            <div class="px-6 py-8">
              <div class="flex flex-col items-center gap-6">
                <!-- Crop Area -->
                <div
                  class="relative border-4 border-gray-300 rounded-lg overflow-hidden cursor-move bg-gray-100"
                  :style="{ width: CROP_SIZE + 'px', height: CROP_SIZE + 'px' }"
                  @mousedown="handleMouseDown"
                >
                  <canvas
                    ref="canvasRef"
                    class="w-full h-full"
                  ></canvas>

                  <!-- Crosshair guides -->
                  <div class="absolute inset-0 pointer-events-none">
                    <div class="absolute top-1/2 left-0 right-0 h-px bg-white opacity-30"></div>
                    <div class="absolute left-1/2 top-0 bottom-0 w-px bg-white opacity-30"></div>
                  </div>

                  <!-- Corner guides -->
                  <div class="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-white opacity-50"></div>
                  <div class="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-white opacity-50"></div>
                  <div class="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-white opacity-50"></div>
                  <div class="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-white opacity-50"></div>
                </div>

                <!-- Zoom Controls -->
                <div class="flex items-center gap-4">
                  <button
                    type="button"
                    @click="handleZoomOut"
                    class="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    :disabled="isUploading"
                  >
                    <span class="material-symbols-outlined">zoom_out</span>
                  </button>

                  <div class="text-sm text-gray-600 font-medium min-w-16 text-center">
                    {{ Math.round(scale * 100) }}%
                  </div>

                  <button
                    type="button"
                    @click="handleZoomIn"
                    class="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    :disabled="isUploading"
                  >
                    <span class="material-symbols-outlined">zoom_in</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="border-t border-gray-200 px-6 py-4">
              <div class="flex items-center justify-end gap-3">
                <button
                  type="button"
                  @click="handleCancel"
                  class="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  :disabled="isUploading"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  @click="handleUpload"
                  class="px-6 py-2.5 text-white bg-brand-blue hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  :disabled="isUploading"
                >
                  <span v-if="isUploading" class="material-symbols-outlined animate-spin">progress_activity</span>
                  <span>{{ isUploading ? 'Uploading...' : 'Upload' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
